<script lang="ts">
	import { type ContentPage } from '$comp/lessons/sidebar/LessonSidebarTypes';

	import Icon from '$shared/icons/Icon.svelte';
	import ActionButton from '$shared/action-button/ActionButton.svelte';
	import { eventBus } from '$shared/lib/eventBus';
	import { onMount } from 'svelte';
	import { iconNameForContentType, sidebarCardClass, eyebrowForContentType } from '$comp/lessons/sidebar/LessonSidebarShared';

	export let lessonId: string | number = '';
	export let cardLesson: ContentPage = {
		name: '',
		content_type: ''
	};
	export let parent: any = {};
	export let cardSelected: boolean = false;
	export let cardIconName: string = '';
	export let cardEyebrow: string = '';
	export let nestedLevel: number = 0;
	export let translations = null;

	const determineCardHed = (cardLesson: ContentPage) => {
		if (cardLesson.content_type === 'Course') {
			if (cardLesson.name) return cardLesson.name
			else if (cardLesson.subtitle) return cardLesson.subtitle
			else return parent.group_name
		}
		else {
			if (cardLesson.subtitle) return cardLesson.subtitle
			else if (cardLesson.name) return cardLesson.name
			else return parent.group_name
		}
	}

	const scrollCurrentCardIntoView = () => {
		let currentCard = document.querySelector('.sidebar__card--current');
		if (currentCard) {
			const scrollableSection = document.querySelector('.sidebar__scrollable');
			if (!scrollableSection || !currentCard) return;

			const scrollableSectionDistanceToTop = scrollableSection.getBoundingClientRect().top;
			const currentCardDistanceToTop = currentCard.getBoundingClientRect().top;
			const manualOffsetFromTop = 24;

			scrollableSection.scrollBy(0, currentCardDistanceToTop - scrollableSectionDistanceToTop - manualOffsetFromTop)
		}
	}

	const idInCollection = (id: any, collection: any) => {
		return collection.find((item: any) => {
			return item.id == id;
		});
	}

	onMount(() => {
		if (cardLesson.content_pages) {
			cardLesson.collapsed = true;
			// E.g. currently viewing a video lesson in a curation section
			if (idInCollection(lessonId, cardLesson.content_pages)) {
				cardLesson.collapsed = false;
			}
	 		cardLesson.content_pages.forEach(childPage => {
	 			if (!childPage.content_pages) return;
	 			// E.g. current viewing a course intro or learning path lesson in a curation section
			 	if (idInCollection(lessonId, childPage.content_pages)) {
					cardLesson.collapsed = false;
				}
				childPage.content_pages.forEach(grandchildPage => {
					if (!grandchildPage.content_pages) return;
					// E.g. currently viewing a lesson in a class in a course in a curation section
			 		if (idInCollection(lessonId, grandchildPage.content_pages)) {
						cardLesson.collapsed = false;
					}
				});
 			});
		}

		if (cardLesson.id == lessonId) {
			scrollCurrentCardIntoView();
		}
		eventBus.on('sidebar-open', (event) => {
			if (cardLesson.id == lessonId) {
				// Delay scroll until after DOM loads
				setTimeout(() => {
				  	scrollCurrentCardIntoView();
				});
			}
		});

		return () => {
			eventBus.off('sidebar-open');
		}
	});
</script>

{#if cardLesson.content_pages}
	<section class={`sidebar__section sidebar__section--nested sidebar__section--nested--${nestedLevel} ${cardLesson.collapsed ? 'sidebar__section--collapsed' : ''}`}>
		<ActionButton
			on:click={() => cardLesson.collapsed = !cardLesson.collapsed}
			design="menu-button"
			buttonClasses="sidebar-section__collapse-button"
		>
			{#if cardLesson.content_type === 'Course' || cardLesson.content_type === 'LearningPath'}
				<div class={`sidebar__card ${cardLesson.completed ? 'sidebar__card--complete' : ''}`}>
					<div class="sidebar__card__icon">
						{#if cardLesson.completed && cardLesson.id != lessonId}
							<Icon icon="Check" size="small"></Icon>
						{:else}
				   		<Icon icon="Map" size="extra-small"></Icon>
						{/if}
					</div>
					<div class="sidebar__card__contents">
						<span class="sidebar__card__eyebrow">{eyebrowForContentType(cardLesson, translations)}</span>
						<h3>{determineCardHed(cardLesson)}</h3>
						<span class="sidebar__card__subhed">{cardLesson.duration}</span>
					</div>
				</div>
			{:else if cardLesson.name}
				<span class="sidebar__section__toggle__text">{cardLesson.name}</span>
			{:else}
				<span class="sidebar__section__toggle__text">Curation section</span>
			{/if}
			<Icon icon={cardLesson.collapsed ? 'ChevronDown' : 'ChevronUp'} size="medium" iconClass="self-start"></Icon>
		</ActionButton>
		<div class="sidebar__section__contents">
			{#each cardLesson.content_pages as sublesson}
				<svelte:self
					lessonId={lessonId}
					parent={cardLesson}
					cardLesson={sublesson}
					cardIconName={iconNameForContentType(sublesson.content_type)}
					cardEyebrow={eyebrowForContentType(sublesson, translations)}
					nestedLevel={nestedLevel + 1}
					translations={translations}
				/>
			{/each}
		</div>
	</section>
{:else}
<a href={cardLesson.url} class={sidebarCardClass(cardLesson, lessonId)} data-selected={cardSelected}>
	<div class="sidebar__card__icon">
		{#if cardLesson.completed && cardLesson.id != lessonId}
			<Icon icon="Check" size="small"></Icon>
		{:else}
   		<Icon icon={cardIconName} size="extra-small"></Icon>
		{/if}
	</div>
	<div class="sidebar__card__contents">
		<span class="sidebar__card__eyebrow">{cardEyebrow}</span>
		<h3>{determineCardHed(cardLesson)}</h3>
		<span class="sidebar__card__subhed">{cardLesson.duration}</span>
	</div>
</a>
{/if}

<style>
	.sidebar__card {
		max-width: 100%;
		padding: .5rem;
		margin-bottom: .5rem;
		background: white;
		border-radius: 8px;
		border: 1px solid transparent;
		display: grid;
		grid-template-columns: 1.5rem auto;
		gap: .5rem;
		&:first-child {
			margin-top: 1rem;
		}
		&.sidebar__card--current {
			background: theme('colors.pale-yellow');
			border-color: theme('colors.yellow');
		}
	}
	.sidebar__card--path {
		padding: 0;
		.sidebar__card__contents {
			grid-column: 1 / span 2;
		}
	}
	.sidebar__card--current {
		.sidebar__card__icon {
			border-color: transparent;
			background: theme('colors.yellow');
			color: theme('colors.pale-yellow');
		}
	}
	.sidebar__card--complete {
		.sidebar__card__icon {
			border-color: transparent;
			background: theme('colors.green.400');
			color: theme('colors.green.100');
		}
	}
	.sidebar__card__icon {
		grid-column: 1 / span 1;
		align-self: center;
		display: grid;
		place-content: center;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		border: 1px solid black;
		position: relative;
	}
	.sidebar__card__contents {
		grid-column: 2 / span 1;
	}
	.sidebar__card__eyebrow {
		color: theme('colors.neutral.500');
		font-size: 12px;
		letter-spacing: 1px;
		line-height: 16px;
		text-transform: uppercase;
		display: block;
	}
	.sidebar__card h3 {
		color: black;
		font-size: 16px;
		line-height: 1.5;
		margin: 0;
		display: block;
	}
	.sidebar__card--current h3 {
		font-weight: 600;
	}
	.sidebar__card__subhed {
		color: theme('colors.neutral.500');
		font-size: 12px;
		line-height: 16px;
		font-weight: 400;
		display: block;
	}

	.sidebar__section--nested--0 {
		.sidebar__section__contents {
			padding-left: 1rem;
			padding-right: 1rem;
		}
	}
	.sidebar__section--nested--1 {
		.sidebar__section__contents {
			padding-left: 1rem;
			padding-right: 1rem;
		}
	}
	.sidebar__section--collapsed .sidebar__section__contents {
		display: none;
	}

	.sidebar__section__toggle__text {
		line-height: 24px;
		font-weight: 500;
		font-size: 1rem;
	}

	:global(.sidebar__section:not(:last-child) .sidebar__card:last-child) {
		margin-bottom: .5rem;
	}
	/* doubled class as intentional specificity hack */
	:global(.sidebar__section__contents .sidebar__card.sidebar__card:first-child) {
		margin-top: 0;
	}
</style>
